import { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@artstorefronts/ui';
import {
  POINT_EVENT_MAX_DAYS,
  POINT_EVENT_MULTIPLIER_MAX,
  POINT_EVENT_MULTIPLIER_MIN,
  POINT_EVENT_SCOPES,
  POINT_EVENT_SCOPE_LABEL,
  type PointEventScope,
} from '@arthelper/shared/bounties/point-events';

const ADMIN_POINT_EVENTS = gql`
  query AdminPointEvents {
    adminPointEvents {
      id
      name
      multiplier
      scopes
      startsAt
      endsAt
    }
  }
`;

const ADMIN_CREATE_POINT_EVENT = gql`
  mutation AdminCreatePointEvent($input: PointEventInput!) {
    adminCreatePointEvent(input: $input) {
      id
    }
  }
`;

const ADMIN_DELETE_POINT_EVENT = gql`
  mutation AdminDeletePointEvent($id: ID!) {
    adminDeletePointEvent(id: $id)
  }
`;

type PointEventRow = {
  id: string;
  name: string;
  multiplier: number;
  scopes: PointEventScope[];
  startsAt: string;
  endsAt: string;
};

const MULTIPLIERS = Array.from({ length: POINT_EVENT_MULTIPLIER_MAX - POINT_EVENT_MULTIPLIER_MIN + 1 }, (_, i) =>
  String(POINT_EVENT_MULTIPLIER_MIN + i)
);

function localValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The coming weekend in the admin's time zone: Saturday 00:00 to Monday 00:00. */
function comingWeekend(): { starts: string; ends: string } {
  const saturday = new Date();
  saturday.setDate(saturday.getDate() + ((6 - saturday.getDay() + 7) % 7 || 7));
  saturday.setHours(0, 0, 0, 0);
  const monday = new Date(saturday);
  monday.setDate(saturday.getDate() + 2);
  return { starts: localValue(saturday), ends: localValue(monday) };
}

function running(row: PointEventRow): boolean {
  return new Date(row.startsAt).getTime() <= Date.now();
}

/**
 * POINTS EVENTS — "a double points weekend" (Taylor, 2026-11-03). A timed, site-wide
 * multiplier on the payouts ticked. It never stacks with a boosted post: the larger applies.
 */
export function PointEvents() {
  const { data, refetch } = useQuery<{ adminPointEvents: PointEventRow[] }>(ADMIN_POINT_EVENTS, {
    fetchPolicy: 'cache-and-network',
  });
  const [create, { loading: creating }] = useMutation(ADMIN_CREATE_POINT_EVENT);
  const [remove] = useMutation(ADMIN_DELETE_POINT_EVENT);
  const weekend = comingWeekend();
  const [name, setName] = useState('Double points weekend');
  const [multiplier, setMultiplier] = useState('2');
  const [scopes, setScopes] = useState<PointEventScope[]>([...POINT_EVENT_SCOPES]);
  const [starts, setStarts] = useState(weekend.starts);
  const [ends, setEnds] = useState(weekend.ends);
  const events = data?.adminPointEvents ?? [];

  const toggle = (scope: PointEventScope) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  const handleCreate = async () => {
    try {
      await create({
        variables: {
          input: {
            name,
            multiplier: Number(multiplier),
            scopes,
            startsAt: new Date(starts).toISOString(),
            endsAt: new Date(ends).toISOString(),
          },
        },
      });
      toast.success('Points event scheduled.');
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to schedule');
    }
  };

  const handleDelete = async (row: PointEventRow) => {
    try {
      await remove({ variables: { id: row.id } });
      toast.success(running(row) ? 'Ended.' : 'Cancelled.');
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to end it');
    }
  };

  return (
    <div className="mb-8 space-y-4 rounded-lg border border-gray-200 bg-white p-4" data-testid="point-events">
      <div>
        <p className="text-sm font-semibold text-gray-900">Points events</p>
        <p className="text-xs text-gray-500">
          A double points weekend, or any timed multiplier, on the payouts you tick. Up to {POINT_EVENT_MAX_DAYS} days.
          A boosted post earns the larger of the two, never both.
        </p>
      </div>

      {events.length > 0 ? (
        <ul className="divide-y rounded-md border" data-testid="point-events-list">
          {events.map((row) => (
            <li key={row.id} className="flex items-start justify-between gap-2 p-3">
              <div className="min-w-0 text-sm">
                <p className="font-medium text-gray-900">
                  {row.multiplier}× · {row.name}{' '}
                  <span className={running(row) ? 'text-green-700' : 'text-gray-500'}>
                    {running(row) ? '· running' : '· scheduled'}
                  </span>
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(row.startsAt).toLocaleString()} → {new Date(row.endsAt).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  {row.scopes.map((scope) => POINT_EVENT_SCOPE_LABEL[scope]).join(' · ')}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label={`${running(row) ? 'End' : 'Cancel'} ${row.name}`}
                onClick={() => void handleDelete(row)}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_100px]">
        <div className="space-y-1">
          <Label htmlFor="pe-name">Name</Label>
          <Input id="pe-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Multiplier</Label>
          <Select value={multiplier} onValueChange={setMultiplier}>
            <SelectTrigger data-testid="point-event-multiplier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MULTIPLIERS.map((choice) => (
                <SelectItem key={choice} value={choice}>
                  {choice}×
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="pe-starts">Starts</Label>
          <Input id="pe-starts" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="pe-ends">Ends</Label>
          <Input id="pe-ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} />
        </div>
      </div>
      <fieldset className="space-y-1">
        <legend className="text-sm font-medium text-gray-900">Multiplies</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {POINT_EVENT_SCOPES.map((scope) => (
            <label key={scope} className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggle(scope)} />
              {POINT_EVENT_SCOPE_LABEL[scope]}
            </label>
          ))}
        </div>
      </fieldset>
      <Button size="sm" disabled={creating || scopes.length === 0} onClick={() => void handleCreate()}>
        Schedule
      </Button>
    </div>
  );
}
